import { moveItemInArray } from '@angular/cdk/drag-drop';
import { on } from '@ngrx/store';
import { EventAttendeeRoles, eventTypeInfoMapV2 } from 'src/app/global.constants';
import { getRequiredLocationsTypes } from 'src/app/job/job-helpers';
import { JobToolState, JobsV2EventLocation, getAttendeeRole } from 'src/app/job/job-tool.reducer';
import { EventStatuses } from 'src/app/schedules/dispatch/store/dispatch.reducer';
import { LoadingState } from 'src/app/utilities/state.util';

import { CalendarEvent_LocationFragment } from '../../../../../generated/graphql.generated';

import { CreateEventDialogActions } from './timeline-create-event.actions';
import { EventType } from './timeline-create-event.component';
import { jobCreateEventDialogInitialState } from './timeline-create-event.state';

export const jobCreateEventDialogReducers = [
    on(CreateEventDialogActions.defaultLocationsLoaded, (state: JobToolState, { defaultLocations }): JobToolState => ({
      ...state,
      createEventDialogVisible: true,
      createEventDialog: {
        ...state.createEventDialog,
        locations: defaultLocations,
      },
    })),
    on(CreateEventDialogActions.eventLoaded, (state: JobToolState, { event }): JobToolState => {

      const { title, type, status, assets = [], id } = event;

      return {
        ...state,
        createEventDialogVisible: true,
        createEventDialog: {
          selectedEventId: id,
          title,
          eventType: type as EventType,
          status: status as EventStatuses,
          assets,
          locations: (event.locations || []).map(formatEventLocation),
          attendees: event.attendees.filter(({ role }) => role !== EventAttendeeRoles.author),
          assetSuggestions: [],
          attendeeSuggestions: [],
          validationErrors: {},
          eventTypeDisabled: true,
        }
      }
    }),
    on(CreateEventDialogActions.closeCreateEventDialogButtonClicked,
      (state: JobToolState): JobToolState => ({
          ...state,
          createEventDialogVisible: false,
          createEventDialog: {
            selectedEventId: undefined,
            eventType: undefined,
            title: '',
            status: undefined,
            assets: [],
            locations: [],
            attendees: [],
            assetSuggestions: [],
            attendeeSuggestions: [],
            validationErrors: {},
            eventTypeDisabled: false,
          }
        }),
    ),
    on(CreateEventDialogActions.mutateEventButtonClicked, (state: JobToolState): JobToolState => {

      return {
        ...state,
        callState: {
          ...state.callState,
          Events: LoadingState.MUTATING,
        },
      }
    }),
    on(CreateEventDialogActions.eventTypeSelected,
      (state: JobToolState, { eventType }): JobToolState => {

        let title = state.createEventDialog.title;

        if (!state.createEventDialog.selectedEventId) {
          title = eventTypeInfoMapV2[eventType].name;
        }

        const requiredLocationsTypes = getRequiredLocationsTypes(eventType, eventTypeInfoMapV2);

        const defaultEventLocations = state.createEventDialog.locations.filter(location =>
          requiredLocationsTypes.includes(location.type)
        );

        return {
          ...state,
          createEventDialog: {
            ...state.createEventDialog,
            locations: defaultEventLocations,
            eventType,
            title,
          }
        }
      },
    ),
    on(CreateEventDialogActions.eventStatusSelected,
      (state: JobToolState, { status }): JobToolState => ({
          ...state,
          createEventDialog: {
            ...state.createEventDialog,
            status,
          },
        }),
    ),
    on(CreateEventDialogActions.eventTitleInputChanged,
      (state: JobToolState, { eventTitle }): JobToolState => ({
          ...state,
          createEventDialog: {
            ...state.createEventDialog,
            title: eventTitle,
          },
        }),
    ),
    on(CreateEventDialogActions.assetSelected,
      (state: JobToolState, { selectedAsset }): JobToolState => ({
          ...state,
          createEventDialog: {
            ...state.createEventDialog,
            assets: [ ...state.createEventDialog.assets, selectedAsset ],
          },
        }),
    ),
    on(CreateEventDialogActions.assetUnselected,
      (state: JobToolState, { unselectedAsset }): JobToolState => ({
          ...state,
          createEventDialog: {
            ...state.createEventDialog,
            assets: state.createEventDialog.assets.filter((a) => a.id !== unselectedAsset.id ),
          },
        }),
    ),
    on(CreateEventDialogActions.locationsSelectionChanged,
      (state: JobToolState, { locations }): JobToolState => ({
          ...state,
          createEventDialog: {
            ...state.createEventDialog,
            locations,
          },
        }),
    ),
    on(CreateEventDialogActions.attendeesSelected,
      (state: JobToolState, { selectedAttendee }): JobToolState => {
        return {
          ...state,
          createEventDialog: {
            ...state.createEventDialog,
            attendees: [ ...state.createEventDialog.attendees, selectedAttendee ],
          }
        }
      },
    ),
    on(CreateEventDialogActions.attendeesUnselected,
      (state: JobToolState, { unselectedAttendee }): JobToolState => ({
          ...state,
          createEventDialog: {
            ...state.createEventDialog,
            attendees: state.createEventDialog.attendees.filter((a) => a.user.id !== unselectedAttendee.user.id),
          },
        }),
    ),
    on(CreateEventDialogActions.locationDropped,
      (state: JobToolState, { previousIndex, currentIndex }): JobToolState => {

        const locations = [ ...state.createEventDialog.locations ];

        moveItemInArray(locations, previousIndex, currentIndex);

        return {
          ...state,
          createEventDialog: {
            ...state.createEventDialog,
            locations,
          },
        };

      },
    ),
    on(CreateEventDialogActions.locationDurationInputChanged,
      (state: JobToolState, { locationType, duration }): JobToolState => {

        const locations = [ ...state.createEventDialog.locations ];

        const locationIndex = locations.findIndex((l) => l.type === locationType);

        const location = {
          ...state.createEventDialog.locations[locationIndex],
          estimatedTimeAtLocation: duration,
        }

        locations.splice(locationIndex, 1, location);

        return {
          ...state,
          createEventDialog: {
            ...state.createEventDialog,
            locations,
          },
        };

      },
    ),
    on(CreateEventDialogActions.searchAssetsSuccess,
      (state: JobToolState, { assets }): JobToolState => ({
          ...state,
          createEventDialog: {
            ...state.createEventDialog,
            assetSuggestions: assets,
          },
        }),
    ),
    on(CreateEventDialogActions.searchAttendeesSuccess,
      (state: JobToolState, { users }): JobToolState => ({
          ...state,
          createEventDialog: {
            ...state.createEventDialog,
            attendeeSuggestions: users.map((user) => ({ role: getAttendeeRole(user), user: user })),
          },
        }),
    ),
    on(
      CreateEventDialogActions.createEventSuccess,
      CreateEventDialogActions.updateEventSuccess,
      (state: JobToolState): JobToolState => {
        return {
          ...state,
          createEventDialogVisible: false,
          createEventDialog: {
            ...jobCreateEventDialogInitialState.createEventDialog,
          },
          callState: {
            ...state.callState,
            Events: LoadingState.MUTATED,
          }
        }
      },
    ),
    on(
      CreateEventDialogActions.createEventError,
      CreateEventDialogActions.updateEventError,
      (state: JobToolState, { error }): JobToolState => {
            return {
              ...state,
              callState: {
                ...state.callState,
                Events: { error },
              }
            }
          },
    ),
] as const;

export function formatEventLocation(location: CalendarEvent_LocationFragment): JobsV2EventLocation {
  return {
    address: location.location.addressLineOne,
    addressLabel: location.location.addressLineOne,
    estimatedTimeAtLocation: location.estimatedTimeAtLocation,
    id: location.id,
    ignoreInDuration: false,
    locationId: location.location?.id,
    order: location.order,
    totalTravelTimeToNextLocation: location.travelTimeToNextLocation + location.travelTimeToNextLocationOffset,
    type: location.type,
  }
}
